import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Tooltip,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  TextField,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdCancel,
} from "react-icons/md";
import { BudgetService } from "../../../../services/budget.service";
import { BudgetInfoComparison } from "../../../../types/budget/comparison-types";
import { BudgetItemTypeEnum } from "../../../../types/budget/budget";
import {
  buildComparisonCategoryTotalRows,
  getComparisonServiceValorTotalDisplay,
  getComparisonServiceValorUnitarioDisplay,
  getComparisonSummaryTaxFlags,
  getExportParityCategorySummaryTotalBrl,
  getExportParityCategorySummaryVu,
  resolveComparisonServiceValues as resolveServiceValues,
  type ComparisonCategoryTotalRowDef,
} from "../../../../utils/budgetComparisonTaxDisplay";
import { formatUnitValue } from "../../../../utils/unitValue";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import {
  comparisonBudgetDisplayName,
  filterBudgetsForBudgetComparison,
  findComparisonBudget,
  type ComparisonModalBudgetList,
} from "../comparison-modal-list";
import { ObsCell } from "../ObsCell";

const C = {
  sectionBg: "#3b668b",
  grandTotalBg: "#274966",
  categoryBg: "#f8f8f8",
  separatorBg: "#e0e0e0",
  border: "#e0e0e0",
  cellText: "#222",
  titleText: "#4b465c",
  descText: "#6f6b7d",
  primary: "#1361a4",
  linkBlue: "#1976d2",
  chipBg: "#e5e8eb",
  controlBorder: "#c4c4c4",
  white: "#ffffff",
};

const LABEL_W = 272;
const SUB_W = 142;
const BUDGET_W = SUB_W * 4;
const HEADER_H = 48;
// Alinhado às quatro primeiras colunas da modal de fechamento / negociação.
const COL_WIDTHS = [180, 58, 82, 142, 136, 180];
/** Mesmos títulos de subcabeçalho do `export-comparative-sheet.service.ts` (modo cliente). */
const COL_LABELS = [
  "Descrição",
  "Qtde",
  "Diárias",
  "Valor Unitário",
  "Valor Total",
  "Obs. Internas",
];
const BUDGET_W_COLS = COL_WIDTHS.reduce((a, b) => a + b, 0);
/** Índices em COL_WIDTHS / COL_LABELS — iguais aos índices do retorno de `getValues`. */
const IDX_VALOR_UNITARIO = 3;
const IDX_VALOR_TOTAL = 4;

const ROW_H: Record<string, number> = {
  "section-header": 48,
  "category-header": 40,
  "column-header": 36,
  service: 32,
  separator: 4,
  "category-total": 32,
  "section-total": 40,
  "grand-total": 48,
};

const SORT_OPTIONS = [
  { value: "DEFAULT", label: "Ordem de criação (padrão)" },
  { value: "ASC", label: "Valor crescente" },
  { value: "DESC", label: "Valor decrescente" },
];

const INFO_ROWS = [
  { key: "local", label: "LOCAL" },
  { key: "city", label: "CIDADE/UF" },
  { key: "country", label: "PAÍS" },
  { key: "address", label: "ENDEREÇO" },
  { key: "eventPeriods", label: "PERÍODO DE HOSPEDAGEM" },
  { key: "meetingPeriod", label: "PERÍODO DE REUNIÃO" },
  { key: "availability", label: "DISPONIBILIDADE" },
  { key: "deadline", label: "DEADLINE" },
  { key: "site", label: "SITE" },
];

type TableRow =
  | { kind: "info-row"; fieldKey: string; label: string }
  | { kind: "section-header"; sectionKey: string; label: string }
  | {
      kind: "category-header";
      sectionKey: string;
      categoryKey: string;
      label: string;
    }
  | { kind: "column-header"; sectionKey: string; categoryKey: string }
  | {
      kind: "service";
      sectionKey: string;
      categoryKey: string;
      serviceType: string;
      slot: number;
      label: string;
    }
  | { kind: "separator"; sectionKey: string; categoryKey: string }
  | {
      kind: "category-total";
      sectionKey: string;
      categoryKey: string;
      totalKey: string;
      label: string;
      isLast: boolean;
    }
  | { kind: "section-total"; sectionKey: string; label: string }
  | { kind: "grand-total"; label: string }
  | { kind: "extras-forecast"; label: string }
  | { kind: "total-with-extras"; label: string }
  | { kind: "gap"; height: number; sectionKey?: string; inSection: boolean };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** List/summary rows for selection labels only — comparison data from export API by id. */
  budgetList: ComparisonModalBudgetList;
}

const fmtCurrency = (v?: number | null) => {
  if (v === null || v === undefined || isNaN(v as number)) return "-";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(v as number);
};

const getCatLabel = (key: string) =>
  BudgetItemTypeEnum[key as keyof typeof BudgetItemTypeEnum] || key;

function buildRows(
  data: BudgetInfoComparison[],
  catTotals: ComparisonCategoryTotalRowDef[]
): TableRow[] {
  const rows: TableRow[] = [];
  if (!data?.length) return rows;

  // ── Info header rows (LOCAL, CIDADE/UF, etc.) ──
  INFO_ROWS.forEach(({ key, label }) =>
    rows.push({ kind: "info-row", fieldKey: key, label })
  );
  rows.push({ kind: "gap", height: 16, inSection: false });

  // ── Build section / category structure ──
  const sectionOrder: string[] = [];
  const sectionTitles = new Map<string, string>();
  const catsBySection = new Map<string, string[]>();

  for (const b of data) {
    for (const sec of b.sections || []) {
      if (!sectionTitles.has(sec.key)) {
        sectionOrder.push(sec.key);
        sectionTitles.set(sec.key, sec.title);
        catsBySection.set(sec.key, []);
      }
      for (const svc of sec.allServices || []) {
        const cats = catsBySection.get(sec.key)!;
        if (!cats.includes(svc.type)) cats.push(svc.type);
      }
    }
  }

  sectionOrder.sort((a, b) => a.localeCompare(b));

  for (const sk of sectionOrder) {
    rows.push({
      kind: "section-header",
      sectionKey: sk,
      label: sectionTitles.get(sk) || sk,
    });
    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });

    const cats = catsBySection.get(sk) || [];
    cats.forEach((ck, ci) => {
      if (ci > 0)
        rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
      rows.push({
        kind: "category-header",
        sectionKey: sk,
        categoryKey: ck,
        label: `${ci + 1}   ${getCatLabel(ck)}`,
      });
      rows.push({ kind: "column-header", sectionKey: sk, categoryKey: ck });

      // ── Slot-based service deduplication (mirrors API getServicesFromCategory) ──
      // Collect serviceTypes in order of first appearance across all budgets
      const seenTypes = new Set<string>();
      const orderedTypes: string[] = [];
      for (const b of data) {
        const sec = b.sections?.find((s) => s.key === sk);
        for (const svc of sec?.allServices?.filter((sv) => sv.type === ck) ||
          []) {
          const st = resolveServiceValues(svc, b.status, b.version)
            ?.serviceType as string | undefined;
          if (st && !seenTypes.has(st)) {
            seenTypes.add(st);
            orderedTypes.push(st);
          }
        }
      }

      let globalIdx = 0;
      for (const st of orderedTypes) {
        // maxCount = max occurrences of this serviceType in any single budget
        let maxCount = 0;
        for (const b of data) {
          const sec = b.sections?.find((s) => s.key === sk);
          const cnt =
            sec?.allServices?.filter(
              (sv) =>
                sv.type === ck &&
                resolveServiceValues(sv, b.status, b.version)?.serviceType ===
                  st
            ).length ?? 0;
          if (cnt > maxCount) maxCount = cnt;
        }
        const displayName =
          (ServiceOptions as any)[ck]?.find((o: any) => o.value === st)?.text ??
          st;
        for (let slot = 0; slot < maxCount; slot++) {
          rows.push({
            kind: "service",
            sectionKey: sk,
            categoryKey: ck,
            serviceType: st,
            slot,
            label: `${globalIdx + 1} ${displayName}`,
          });
          globalIdx++;
        }
      }

      rows.push({ kind: "separator", sectionKey: sk, categoryKey: ck });
      catTotals.forEach((t, ti) =>
        rows.push({
          kind: "category-total",
          sectionKey: sk,
          categoryKey: ck,
          totalKey: t.key,
          label: t.label,
          isLast: ti === catTotals.length - 1,
        })
      );
    });

    rows.push({ kind: "gap", height: 8, sectionKey: sk, inSection: true });
    rows.push({
      kind: "section-total",
      sectionKey: sk,
      label: `[${sk}] TOTAL`,
    });
    rows.push({ kind: "gap", height: 32, sectionKey: sk, inSection: false });
  }

  rows.push({
    kind: "grand-total",
    label: "RESUMO GERAL DOS CUSTOS (A+B+C+D)",
  });
  rows.push({ kind: "extras-forecast", label: "Previsão de Extras" });
  rows.push({ kind: "total-with-extras", label: "Total + Extras" });
  return rows;
}

/** Previsão de extras (percentual e valor em R$) e total com extras — paridade com o painel "Totais". */
function getExtrasInfo(b: BudgetInfoComparison): {
  grandTotal: number;
  extrasBRL: number;
  pctText: string;
} {
  const grandTotal = (b.totals as any)?.totals?.totals?.totalInBRL ?? 0;
  const tax = (b as any)?.budgetWideTaxData?.extras;
  const rawVal = typeof tax?.value === "number" ? tax.value : 0;
  const isPct = tax?.typeTax === "PERCENTAGE" || tax?.typeTax === "%";

  // Valor em R$: prioriza o total de extras já computado pelo backend; senão calcula igual ao painel "Totais".
  const backendBRL = (b.totals as any)?.extras?.totals?.totalInBRL;
  let extrasBRL: number;
  if (typeof backendBRL === "number" && backendBRL !== 0) {
    extrasBRL = backendBRL;
  } else if (isPct) {
    extrasBRL = grandTotal * (rawVal / 100);
  } else if (typeof tax?.valueBRL === "number") {
    extrasBRL = tax.valueBRL;
  } else {
    extrasBRL = rawVal;
  }

  let pctText = "-";
  if (isPct) {
    pctText = Number.isInteger(rawVal) ? `${rawVal}%` : `${rawVal.toFixed(2)}%`;
  }

  return { grandTotal, extrasBRL, pctText };
}

function getValues(
  b: BudgetInfoComparison,
  row: TableRow,
  taxFlags: { showIssSummary: boolean; showIofSummary: boolean }
): [
  string,
  string | number,
  string | number,
  string | number,
  string | number,
  string
] {
  if (row.kind === "info-row") {
    const ci = b.comparisonInfo as any;
    let val: string;
    if (row.fieldKey === "eventPeriods") {
      // Igual ao Excel: formata cada período como "dd/MM/yyyy HH:mm à dd/MM/yyyy HH:mm"
      const periods = ci?.eventPeriods as
        | Array<{ inicio: string; fim: string }>
        | undefined;
      if (periods?.length) {
        val = periods
          .map((p) => {
            try {
              const fmt = (iso: string) =>
                new Date(iso).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              return `${fmt(p.inicio)} à ${fmt(p.fim)}`;
            } catch {
              return `${p.inicio} à ${p.fim}`;
            }
          })
          .join(" | ");
      } else {
        // Fallback: campo legado accommodationPeriod
        val = ci?.accommodationPeriod || "-";
      }
    } else {
      val = ci?.[row.fieldKey] || "-";
    }
    return ["", "", "", "", val, ""];
  }
  if (row.kind === "section-header") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency(s?.sectionTotals?.totals?.totalInBRL),
      "",
    ];
  }
  if (row.kind === "service") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const sameType =
      s?.allServices?.filter(
        (x) =>
          x.type === row.categoryKey &&
          resolveServiceValues(x, b.status, b.version)?.serviceType ===
            row.serviceType
      ) ?? [];
    const svc = sameType[row.slot];
    if (!svc) return ["-", 0, 0, fmtCurrency(0), fmtCurrency(0), "-"];
    const sv = resolveServiceValues(svc, b.status, b.version);
    const typeTax = sv?.taxData?.tariff?.typeTax;
    const isCortesy = typeTax === "CORTESY";
    const isIncluso = typeTax === "INCLUDED";
    const isAcClient = typeTax === "AC_CLIENT";
    const valueText = isCortesy
      ? "Cortesia"
      : isIncluso
      ? "Incluso"
      : "A/C Cliente";
    const vuCell = getComparisonServiceValorUnitarioDisplay(sv, true);
    const valorUn =
      isCortesy || isIncluso || isAcClient
        ? valueText
        : typeof vuCell === "number"
        ? formatUnitValue(vuCell, "BRL")
        : vuCell;
    const vtCell = getComparisonServiceValorTotalDisplay(sv, true);
    const valorTotal =
      isCortesy || isIncluso || isAcClient
        ? valueText
        : typeof vtCell === "number" && Number.isFinite(vtCell)
        ? fmtCurrency(vtCell)
        : typeof vtCell === "string"
        ? vtCell
        : fmtCurrency(0);
    // Descrição do serviço (campo voltado ao cliente) — igual à coluna
    // "Descrição" do Excel comparativo; distinta de "Obs. Internas".
    const descricao = sv?.observation || "-";
    const obs = sv?.internalObservation || "-";
    return [
      descricao,
      sv?.quantity ?? 0,
      sv?.daily ?? 0,
      valorUn,
      valorTotal,
      obs,
    ];
  }
  if (row.kind === "category-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    const ct = (s?.totalsPerCategory as any)?.[row.categoryKey];
    const vuRaw = getExportParityCategorySummaryVu(
      row.totalKey,
      b,
      row.sectionKey,
      row.categoryKey
    );
    const valorUn = vuRaw === "" ? "" : vuRaw;
    const vt = getExportParityCategorySummaryTotalBrl(
      row.totalKey,
      ct,
      taxFlags
    );
    return ["", "-", "-", valorUn, fmtCurrency(vt), ""];
  }
  if (row.kind === "section-total") {
    const s = b.sections?.find((x) => x.key === row.sectionKey);
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency(s?.sectionTotals?.totals?.totalInBRL),
      "",
    ];
  }
  if (row.kind === "grand-total") {
    return [
      "",
      "-",
      "-",
      "-",
      fmtCurrency((b.totals as any)?.totals?.totals?.totalInBRL),
      "",
    ];
  }
  if (row.kind === "extras-forecast") {
    const { extrasBRL, pctText } = getExtrasInfo(b);
    return ["", "-", "-", pctText, fmtCurrency(extrasBRL), ""];
  }
  if (row.kind === "total-with-extras") {
    const { grandTotal, extrasBRL } = getExtrasInfo(b);
    return ["", "-", "-", "-", fmtCurrency(grandTotal + extrasBRL), ""];
  }
  return ["", "", "", "", "", ""];
}

function isRowVisible(
  row: TableRow,
  collSec: Set<string>,
  collCat: Set<string>
): boolean {
  if (
    row.kind === "grand-total" ||
    row.kind === "section-header" ||
    row.kind === "info-row" ||
    row.kind === "extras-forecast" ||
    row.kind === "total-with-extras"
  )
    return true;
  if (row.kind === "column-header") {
    const sk = row.sectionKey;
    if (sk && collSec.has(sk)) return false;
    const ck = row.categoryKey;
    if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;
    return true;
  }

  if (row.kind === "gap") {
    if (!row.inSection) return true;
    return !(row.sectionKey && collSec.has(row.sectionKey));
  }

  const sk = (row as any).sectionKey as string | undefined;
  if (sk && collSec.has(sk)) return false;

  if (row.kind === "category-header") return true;

  const ck = (row as any).categoryKey as string | undefined;
  if (sk && ck && collCat.has(`${sk}||${ck}`)) return false;

  return true;
}

const FONT = "'Public Sans', sans-serif";
const bdr = `1px solid ${C.border}`;
/** Divisória vertical entre cada bloco de orçamento (igual comparativo de negociação). */
const GROUP_DIVIDER_R = "3px solid #7A909F";
/** Linhas internas nas faixas azul / total escuro */
const SECTION_INNER_R = "1px solid rgba(255,255,255,0.32)";

function budgetBlockCellBorderRight(
  bi: number,
  ci: number,
  numBudgets: number
): string {
  if (ci < COL_WIDTHS.length - 1) return bdr;
  return bi === numBudgets - 1 ? bdr : GROUP_DIVIDER_R;
}

function budgetSectionInnerBorderRight(
  bi: number,
  ci: number,
  numBudgets: number
): string {
  if (ci < COL_WIDTHS.length - 1) return SECTION_INNER_R;
  return bi === numBudgets - 1 ? bdr : GROUP_DIVIDER_R;
}

const BudgetComparisonModal: React.FC<Props> = ({
  isOpen,
  onClose,
  budgetList,
}) => {
  const PAGE_SIZE = 10;
  const [step, setStep] = useState<"selection" | "comparison">("selection");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [compData, setCompData] = useState<BudgetInfoComparison[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState("DEFAULT");
  const [collSec, setCollSec] = useState<Set<string>>(new Set());
  const [collCat, setCollCat] = useState<Set<string>>(new Set());
  const [selectionPage, setSelectionPage] = useState(1);

  useEffect(() => {
    if (!isOpen) {
      setStep("selection");
      setSelectedIds([]);
      setCompData([]);
      setError(null);
      setLoading(false);
      setSortOrder("DEFAULT");
      setCollSec(new Set());
      setCollCat(new Set());
      setSelectionPage(1);
    }
  }, [isOpen]);

  // FE-P1-09: selection from summary-safe list fields; payloads from generateComparisonInfo(ids).
  const available = useMemo(
    () => filterBudgetsForBudgetComparison(budgetList),
    [budgetList]
  );

  const fetchComp = useCallback(async (ids: string[], sort: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await BudgetService.generateComparisonInfo({
        ids: ids.join(","),
        exportType: "CLIENT",
        sortOrder: sort,
      });
      setCompData(r || []);
      return true;
    } catch {
      setError("Não foi possível carregar a comparação. Tente novamente.");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAdvance = async () => {
    if (await fetchComp(selectedIds, sortOrder)) setStep("comparison");
  };

  const refresh = (ids: string[], sort: string) => fetchComp(ids, sort);

  const toggleBudget = (id: string) =>
    setSelectedIds((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );

  const resetSel = () => setSelectedIds([]);

  const removeBudget = (id: string) => {
    if (selectedIds.length <= 1) return;
    const next = selectedIds.filter((x) => x !== id);
    setSelectedIds(next);
    refresh(next, sortOrder);
  };

  const addBudget = (id: string) => {
    if (selectedIds.includes(id)) return;
    const next = [...selectedIds, id];
    setSelectedIds(next);
    refresh(next, sortOrder);
  };

  const changeSort = (v: string) => {
    setSortOrder(v);
    refresh(selectedIds, v);
  };

  const budgetName = (id: string) =>
    comparisonBudgetDisplayName(available.find((x) => x._id === id));

  const toggleSection = useCallback((key: string) => {
    setCollSec((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((sKey: string, cKey: string) => {
    const k = `${sKey}||${cKey}`;
    setCollCat((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const sortedCompData = useMemo(() => {
    const getIndex = (budgetId: string) =>
      findComparisonBudget(budgetList, budgetId)?.budgetIndex ?? 0;

    if (sortOrder === "DEFAULT") {
      return [...compData].sort(
        (a, b) => getIndex(a.budgetId) - getIndex(b.budgetId)
      );
    }
    return [...compData].sort((a, b) => {
      const va = (a.totals as any)?.totals?.totals?.totalInBRL ?? 0;
      const vb = (b.totals as any)?.totals?.totals?.totalInBRL ?? 0;
      return sortOrder === "ASC" ? va - vb : vb - va;
    });
  }, [compData, sortOrder, budgetList]);

  const comparisonTaxFlags = useMemo(
    () => getComparisonSummaryTaxFlags(sortedCompData),
    [sortedCompData]
  );

  const categoryTotalRows = useMemo(
    () => buildComparisonCategoryTotalRows(comparisonTaxFlags),
    [comparisonTaxFlags]
  );

  const tableRows = useMemo(
    () => buildRows(sortedCompData, categoryTotalRows),
    [sortedCompData, categoryTotalRows]
  );
  const firstSectionHeaderRowIndex = useMemo(
    () => tableRows.findIndex((r) => r.kind === "section-header"),
    [tableRows]
  );
  const unselected = useMemo(
    () => available.filter((b) => !selectedIds.includes(b._id)),
    [available, selectedIds]
  );

  // Effective budget block width based on content-fitted COL_WIDTHS
  const effectiveSUB_W = SUB_W; // kept for RegularDataCell fallback
  const effectiveBUDGET_W = BUDGET_W_COLS;
  const tableW = LABEL_W + sortedCompData.length * effectiveBUDGET_W;

  // ── Selection modal ────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={isOpen && step === "selection"}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 680,
            maxWidth: "95vw",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
          },
        }}
      >
        <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 24,
                  fontWeight: 400,
                  color: C.titleText,
                  lineHeight: "32px",
                  fontFamily: FONT,
                }}
              >
                Pré-visualizar orçamentos
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: C.titleText }}
              >
                <IoMdClose size={20} />
              </IconButton>
            </Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 400,
                color: C.descText,
                lineHeight: "22px",
                fontFamily: FONT,
              }}
            >
              Selecione abaixo os orçamentos desejados. Caso mais de 2
              orçamentos sejam selecionados, será mostrada a comparação entre
              eles.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {available.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhum orçamento disponível.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    border: `1px solid ${C.border}`,
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  {available
                    .slice(
                      (selectionPage - 1) * PAGE_SIZE,
                      selectionPage * PAGE_SIZE
                    )
                    .map((b, idx, arr) => (
                      <FormControlLabel
                        key={b._id}
                        control={
                          <Checkbox
                            checked={selectedIds.includes(b._id)}
                            onChange={() => toggleBudget(b._id)}
                            size="small"
                            sx={{
                              color: C.primary,
                              "&.Mui-checked": { color: C.primary },
                              p: "2px",
                              mr: 1,
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: C.cellText,
                              fontFamily: FONT,
                            }}
                          >
                            {b.name || `Orçamento ${(b.budgetIndex ?? 0) + 1}`}
                          </Typography>
                        }
                        sx={{
                          ml: 0,
                          mr: 0,
                          width: "100%",
                          px: 2,
                          py: 0.75,
                          borderBottom:
                            idx < arr.length - 1
                              ? `1px solid ${C.border}`
                              : "none",
                          "&:hover": { bgcolor: "rgba(19,97,164,0.04)" },
                        }}
                      />
                    ))}
                </Box>
                {available.length > PAGE_SIZE && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pt: 1,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: C.descText, fontFamily: FONT }}
                    >
                      {`${(selectionPage - 1) * PAGE_SIZE + 1}–${Math.min(
                        selectionPage * PAGE_SIZE,
                        available.length
                      )} de ${available.length}`}
                    </Typography>
                    <Pagination
                      count={Math.ceil(available.length / PAGE_SIZE)}
                      page={selectionPage}
                      onChange={(_, p) => setSelectionPage(p)}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontFamily: FONT,
                          fontSize: 12,
                        },
                        "& .Mui-selected": {
                          bgcolor: `${C.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="text"
              onClick={resetSel}
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: C.linkBlue,
                textTransform: "none",
                px: 1.25,
                height: 40,
                fontFamily: FONT,
                bgcolor: "transparent",
                "&:hover": { bgcolor: "rgba(25,118,210,0.04)" },
              }}
            >
              Redefinir seleção
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: C.linkBlue,
                borderColor: C.primary,
                textTransform: "none",
                px: 1.25,
                borderRadius: "8px",
                height: 40,
                fontFamily: FONT,
                "&:hover": {
                  borderColor: C.primary,
                  bgcolor: "rgba(19,97,164,0.04)",
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleAdvance}
              disabled={selectedIds.length < 1 || loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                fontWeight: 600,
                fontSize: 14,
                bgcolor: C.primary,
                textTransform: "none",
                px: 1.25,
                borderRadius: "8px",
                height: 40,
                fontFamily: FONT,
                "&:hover": { bgcolor: "#0f4f89" },
                "&.Mui-disabled": { bgcolor: "#a0bfd8", color: "#fff" },
              }}
            >
              Avançar
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Comparison modal ──────────────────────────────────────────── */}
      <Dialog
        open={isOpen && step === "comparison"}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": { margin: "56px" },
          "& .MuiDialog-container": { padding: 0 },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 112px)",
            maxWidth: "calc(100% - 112px)",
            height: "calc(100% - 112px)",
            maxHeight: "calc(100% - 112px)",
            borderRadius: "8px",
            boxShadow: "0px 4px 18px rgba(75,70,92,0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 4,
            pt: 4,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 400,
              color: C.titleText,
              lineHeight: "32px",
              fontFamily: FONT,
            }}
          >
            Pré-visualizar orçamentos
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: C.titleText }}
          >
            <IoMdClose size={20} />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 4,
            pt: 4,
            pb: 4,
            display: "flex",
            gap: 1,
            alignItems: "flex-end",
            flexShrink: 0,
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{ fontSize: 13, color: C.cellText, fontFamily: FONT }}
            >
              Comparar
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                bgcolor: C.white,
                border: `1px solid ${C.controlBorder}`,
                borderRadius: "6px",
                height: 32,
                px: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                  flexWrap: "wrap",
                  overflow: "hidden",
                }}
              >
                {selectedIds.map((id) => (
                  <Tooltip
                    key={id}
                    title={
                      selectedIds.length <= 1 ? "Mínimo de 1 orçamento" : ""
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        bgcolor: C.chipBg,
                        borderRadius: "100px",
                        pl: "8px",
                        pr: "4px",
                        height: 24,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: C.cellText,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                          fontFamily: FONT,
                        }}
                      >
                        {budgetName(id)}
                      </Typography>
                      <Box
                        component="span"
                        onClick={() => removeBudget(id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor:
                            selectedIds.length > 1 ? "pointer" : "default",
                          color: selectedIds.length > 1 ? "#555" : "#aaa",
                          flexShrink: 0,
                        }}
                      >
                        <MdCancel size={16} />
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
                {unselected.length > 0 && (
                  <Autocomplete
                    size="small"
                    options={unselected}
                    getOptionLabel={(b) => comparisonBudgetDisplayName(b)}
                    value={null}
                    onChange={(_, v) => {
                      if (v?._id) addBudget(v._id);
                    }}
                    disablePortal
                    sx={{
                      minWidth: 120,
                      flexShrink: 0,
                      "& .MuiOutlinedInput-root": { p: 0, border: "none" },
                      "& fieldset": { border: "none" },
                    }}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        placeholder="Adicionar..."
                        variant="standard"
                        InputProps={{ ...p.InputProps, disableUnderline: true }}
                        sx={{
                          "& input": {
                            fontSize: 12,
                            color: "#999",
                            fontFamily: FONT,
                            p: 0,
                          },
                        }}
                      />
                    )}
                    noOptionsText="Nenhum disponível"
                    isOptionEqualToValue={(o: any, v: any) => o._id === v._id}
                    ListboxProps={{ style: { maxHeight: 360 } }}
                    componentsProps={{
                      paper: {
                        sx: {
                          minWidth: 280,
                          "& .MuiAutocomplete-option": {
                            fontSize: 13,
                            fontFamily: FONT,
                            py: "6px",
                          },
                        },
                      },
                    }}
                  />
                )}
              </Box>
              <MdKeyboardArrowDown
                size={16}
                color="#666"
                style={{ flexShrink: 0 }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: 228,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{ fontSize: 12, color: C.cellText, fontFamily: FONT }}
            >
              Ordenação
            </Typography>
            <Select
              value={sortOrder}
              onChange={(e) => changeSort(e.target.value)}
              size="small"
              sx={{
                height: 32,
                fontSize: 12,
                fontFamily: FONT,
                color: C.titleText,
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: C.controlBorder,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: C.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: C.primary,
                },
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem
                  key={o.value}
                  value={o.value}
                  sx={{ fontSize: 12, fontFamily: FONT }}
                >
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ m: 2, flexShrink: 0 }}
              action={
                <Button
                  size="small"
                  onClick={() => refresh(selectedIds, sortOrder)}
                >
                  Tentar novamente
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ p: 4 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={40}
                  sx={{ mb: 1, borderRadius: 0.5 }}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {tableRows.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum dado disponível.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "inline-block",
                    marginLeft: "24px",
                    marginTop: "12px",
                    marginBottom: "12px",
                    borderRadius: "10px",
                    overflow: "clip",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.10)",
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                      width: tableW,
                      fontFamily: FONT,
                    }}
                  >
                    {/* ── colgroup: garantia de larguras iguais em todas as linhas ── */}
                    <colgroup>
                      <col style={{ width: LABEL_W }} />
                      {sortedCompData.flatMap((_, bi) =>
                        COL_WIDTHS.map((w, ci) => (
                          <col key={`${bi}-${ci}`} style={{ width: w }} />
                        ))
                      )}
                    </colgroup>

                    {/* ── thead: nomes dos orçamentos (sticky) ── */}
                    <Box component="thead">
                      <Box component="tr">
                        <Box
                          component="th"
                          sx={{
                            position: "sticky",
                            top: 0,
                            left: 0,
                            zIndex: 12,
                            bgcolor: C.white,
                            height: HEADER_H,
                            borderBottom: bdr,
                            borderRight: GROUP_DIVIDER_R,
                            boxShadow: "2px 0 4px rgba(0,0,0,0.04)",
                          }}
                        />
                        {sortedCompData.map((bd, bi) => (
                          <Box
                            component="th"
                            key={bd.budgetId}
                            colSpan={COL_WIDTHS.length}
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                              bgcolor: C.white,
                              height: HEADER_H,
                              borderTop: bdr,
                              borderBottom: bdr,
                              borderRight:
                                bi === sortedCompData.length - 1
                                  ? bdr
                                  : GROUP_DIVIDER_R,
                              borderLeft: "none",
                              textAlign: "center",
                              fontSize: 15,
                              fontWeight: 700,
                              color: C.cellText,
                              fontFamily: FONT,
                            }}
                          >
                            {bd.budgetName}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* ── tbody: todas as linhas de dados ── */}
                    <Box component="tbody">
                      {tableRows.map((row, ri) => {
                        if (!isRowVisible(row, collSec, collCat)) return null;
                        const totalCols =
                          1 + sortedCompData.length * COL_WIDTHS.length;
                        const nb = sortedCompData.length;

                        // ── info-row ──
                        if (row.kind === "info-row") {
                          const isFirst = ri === 0;
                          return (
                            <Box component="tr" key={ri}>
                              <Box
                                component="td"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: C.categoryBg,
                                  borderTop: isFirst ? bdr : undefined,
                                  borderBottom: bdr,
                                  height: 28,
                                  pl: "14px",
                                  pr: "10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: C.cellText,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  borderRight: GROUP_DIVIDER_R,
                                }}
                              >
                                {row.label}
                              </Box>
                              {sortedCompData.map((bd, bi) => {
                                const v = getValues(
                                  bd,
                                  row,
                                  comparisonTaxFlags
                                )[IDX_VALOR_TOTAL];
                                const isLastBudget = bi === nb - 1;
                                return (
                                  <Box
                                    component="td"
                                    key={bd.budgetId}
                                    colSpan={COL_WIDTHS.length}
                                    sx={{
                                      bgcolor: C.categoryBg,
                                      borderTop: isFirst ? bdr : undefined,
                                      borderBottom: bdr,
                                      height: 28,
                                      pl: "14px",
                                      pr: "10px",
                                      fontSize: 12,
                                      color: C.cellText,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      borderRight: isLastBudget
                                        ? bdr
                                        : GROUP_DIVIDER_R,
                                    }}
                                  >
                                    {v}
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        }

                        // ── gap ──
                        if (row.kind === "gap") {
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: row.height }}
                            >
                              <Box
                                component="td"
                                colSpan={totalCols}
                                sx={{ bgcolor: C.white }}
                              />
                            </Box>
                          );
                        }

                        // ── section-header ──
                        if (row.kind === "section-header") {
                          const expanded = !collSec.has(row.sectionKey);
                          const sectionStageDivider =
                            firstSectionHeaderRowIndex >= 0 &&
                            ri > firstSectionHeaderRowIndex
                              ? GROUP_DIVIDER_R
                              : undefined;
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: ROW_H["section-header"] }}
                            >
                              <Box
                                component="td"
                                onClick={() => toggleSection(row.sectionKey)}
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: C.sectionBg,
                                  cursor: "pointer",
                                  pl: "10px",
                                  pr: "8px",
                                  overflow: "hidden",
                                  borderRight: SECTION_INNER_R,
                                  borderTop: sectionStageDivider,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <MdKeyboardArrowDown
                                    size={18}
                                    color={C.white}
                                    style={{
                                      flexShrink: 0,
                                      transform: expanded
                                        ? "none"
                                        : "rotate(-90deg)",
                                      transition: "transform 0.2s",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: C.white,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontFamily: FONT,
                                    }}
                                  >
                                    {row.label}
                                  </Typography>
                                </Box>
                              </Box>
                              {sortedCompData.flatMap((bd, bi) => {
                                const vals = getValues(
                                  bd,
                                  row,
                                  comparisonTaxFlags
                                );
                                return COL_WIDTHS.map((_, ci) => (
                                  <Box
                                    component="td"
                                    key={`${bi}-${ci}`}
                                    sx={{
                                      bgcolor: C.sectionBg,
                                      textAlign: "right",
                                      pr: "10px",
                                      pl: "10px",
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color:
                                        ci === IDX_VALOR_TOTAL
                                          ? C.white
                                          : C.sectionBg,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      borderRight:
                                        budgetSectionInnerBorderRight(
                                          bi,
                                          ci,
                                          nb
                                        ),
                                      borderTop: sectionStageDivider,
                                    }}
                                  >
                                    {ci === IDX_VALOR_TOTAL
                                      ? vals[IDX_VALOR_TOTAL]
                                      : ""}
                                  </Box>
                                ));
                              })}
                            </Box>
                          );
                        }

                        // ── category-header ──
                        if (row.kind === "category-header") {
                          const catKey = `${row.sectionKey}||${row.categoryKey}`;
                          const expanded = !collCat.has(catKey);
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: ROW_H["category-header"] }}
                            >
                              <Box
                                component="td"
                                colSpan={totalCols}
                                onClick={() =>
                                  toggleCategory(
                                    row.sectionKey,
                                    row.categoryKey
                                  )
                                }
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  bgcolor: C.categoryBg,
                                  cursor: "pointer",
                                  borderTop: bdr,
                                  borderBottom: bdr,
                                  pl: "12px",
                                  pr: "8px",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {expanded ? (
                                    <MdKeyboardArrowUp
                                      size={16}
                                      color={C.cellText}
                                      style={{ flexShrink: 0 }}
                                    />
                                  ) : (
                                    <MdKeyboardArrowDown
                                      size={16}
                                      color={C.cellText}
                                      style={{ flexShrink: 0 }}
                                    />
                                  )}
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color: C.cellText,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontFamily: FONT,
                                    }}
                                  >
                                    {row.label}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        }

                        // ── column-header ──
                        if (row.kind === "column-header") {
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: ROW_H["column-header"] }}
                            >
                              <Box
                                component="td"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: C.categoryBg,
                                  borderBottom: bdr,
                                  borderRight: GROUP_DIVIDER_R,
                                }}
                              />
                              {sortedCompData.flatMap((_, bi) =>
                                COL_LABELS.map((col, ci) => (
                                  <Box
                                    component="td"
                                    key={`${bi}-${ci}`}
                                    sx={{
                                      bgcolor: C.categoryBg,
                                      borderBottom: bdr,
                                      borderRight: budgetBlockCellBorderRight(
                                        bi,
                                        ci,
                                        nb
                                      ),
                                      textAlign:
                                        ci === 0 || ci === COL_WIDTHS.length - 1
                                          ? "left"
                                          : "right",
                                      pr: "10px",
                                      pl: "10px",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: C.cellText,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {col}
                                  </Box>
                                ))
                              )}
                            </Box>
                          );
                        }

                        // ── grand-total ──
                        if (row.kind === "grand-total") {
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: ROW_H["grand-total"] }}
                            >
                              <Box
                                component="td"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: C.grandTotalBg,
                                  pl: "12px",
                                  pr: "8px",
                                  overflow: "hidden",
                                  borderRight: SECTION_INNER_R,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: C.white,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontFamily: FONT,
                                  }}
                                >
                                  {row.label}
                                </Typography>
                              </Box>
                              {sortedCompData.flatMap((bd, bi) => {
                                const vals = getValues(
                                  bd,
                                  row,
                                  comparisonTaxFlags
                                );
                                return COL_WIDTHS.map((_, ci) => (
                                  <Box
                                    component="td"
                                    key={`${bi}-${ci}`}
                                    sx={{
                                      bgcolor: C.grandTotalBg,
                                      textAlign: "right",
                                      pr: "10px",
                                      pl: "10px",
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color:
                                        ci === IDX_VALOR_TOTAL
                                          ? C.white
                                          : C.grandTotalBg,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      borderRight:
                                        budgetSectionInnerBorderRight(
                                          bi,
                                          ci,
                                          nb
                                        ),
                                    }}
                                  >
                                    {ci === IDX_VALOR_TOTAL
                                      ? vals[IDX_VALOR_TOTAL]
                                      : ""}
                                  </Box>
                                ));
                              })}
                            </Box>
                          );
                        }

                        // ── extras-forecast / total-with-extras ──
                        if (
                          row.kind === "extras-forecast" ||
                          row.kind === "total-with-extras"
                        ) {
                          const isExtras = row.kind === "extras-forecast";
                          const rowBg = isExtras ? C.white : C.grandTotalBg;
                          const textColor = isExtras ? C.cellText : C.white;
                          return (
                            <Box
                              component="tr"
                              key={ri}
                              sx={{ height: ROW_H["grand-total"] }}
                            >
                              <Box
                                component="td"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: rowBg,
                                  pl: "12px",
                                  pr: "8px",
                                  overflow: "hidden",
                                  borderRight: GROUP_DIVIDER_R,
                                  borderTop: isExtras ? bdr : undefined,
                                  borderBottom: isExtras ? bdr : undefined,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: textColor,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontFamily: FONT,
                                  }}
                                >
                                  {row.label}
                                </Typography>
                              </Box>
                              {sortedCompData.flatMap((bd, bi) => {
                                const vals = getValues(
                                  bd,
                                  row,
                                  comparisonTaxFlags
                                );
                                return COL_WIDTHS.map((_, ci) => {
                                  // extras: % em "Valor Unitário" + R$ em "Valor Total"; total+extras: só "Valor Total"
                                  const show = isExtras
                                    ? ci === IDX_VALOR_UNITARIO ||
                                      ci === IDX_VALOR_TOTAL
                                    : ci === IDX_VALOR_TOTAL;
                                  const cellBorderRight = isExtras
                                    ? budgetBlockCellBorderRight(bi, ci, nb)
                                    : budgetSectionInnerBorderRight(bi, ci, nb);
                                  return (
                                    <Box
                                      component="td"
                                      key={`${bi}-${ci}`}
                                      sx={{
                                        bgcolor: rowBg,
                                        textAlign: "right",
                                        pr: "10px",
                                        pl: "10px",
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: show ? textColor : rowBg,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        borderRight: cellBorderRight,
                                        borderTop: isExtras ? bdr : undefined,
                                        borderBottom: isExtras
                                          ? bdr
                                          : undefined,
                                      }}
                                    >
                                      {show ? vals[ci] : ""}
                                    </Box>
                                  );
                                });
                              })}
                            </Box>
                          );
                        }

                        // ── separator ──
                        if (row.kind === "separator") {
                          return (
                            <Box component="tr" key={ri} sx={{ height: 4 }}>
                              <Box
                                component="td"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 2,
                                  bgcolor: C.separatorBg,
                                  borderRight: GROUP_DIVIDER_R,
                                }}
                              />
                              {sortedCompData.flatMap((_, bi) =>
                                COL_WIDTHS.map((__, ci) => (
                                  <Box
                                    component="td"
                                    key={`${bi}-${ci}`}
                                    sx={{
                                      bgcolor: C.separatorBg,
                                      borderRight: budgetBlockCellBorderRight(
                                        bi,
                                        ci,
                                        nb
                                      ),
                                    }}
                                  />
                                ))
                              )}
                            </Box>
                          );
                        }

                        // ── service / category-total / section-total ──
                        const isSvcRow = row.kind === "service";
                        const isCatTotal = row.kind === "category-total";
                        const isSecTotal = row.kind === "section-total";
                        const isLastCatTotal =
                          isCatTotal && (row as any).isLast;

                        const rowBg = isSecTotal ? C.categoryBg : C.white;
                        const labelSx: any = {
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: rowBg,
                          height: ROW_H[row.kind],
                          pl: "14px",
                          pr: "10px",
                          fontSize: 13,
                          fontFamily: FONT,
                          color: C.cellText,
                          fontWeight: isSecTotal ? 700 : 400,
                          borderBottom: isLastCatTotal ? "none" : bdr,
                          borderTop: isSecTotal ? bdr : "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderRight: GROUP_DIVIDER_R,
                        };

                        return (
                          <Box component="tr" key={ri}>
                            <Box component="td" sx={labelSx}>
                              {(row as any).label}
                            </Box>
                            {sortedCompData.flatMap((bd, bi) => {
                              const vals = getValues(
                                bd,
                                row,
                                comparisonTaxFlags
                              );
                              return COL_WIDTHS.map((_, ci) => {
                                const v = vals[ci];
                                // Descrição e Obs. Internas (as duas últimas colunas) usam o
                                // mesmo tratamento de texto expansível — só as numéricas ficam
                                // à direita.
                                const isTextCol =
                                  ci === 0 || ci === COL_WIDTHS.length - 1;
                                const cellSx: any = {
                                  bgcolor: rowBg,
                                  textAlign: isTextCol ? "left" : "right",
                                  pr: "10px",
                                  pl: "10px",
                                  fontSize: 13,
                                  fontFamily: FONT,
                                  color: C.cellText,
                                  fontWeight: isSecTotal ? 700 : 400,
                                  borderRight: budgetBlockCellBorderRight(
                                    bi,
                                    ci,
                                    nb
                                  ),
                                  borderBottom: isLastCatTotal ? "none" : bdr,
                                  borderTop: isSecTotal ? bdr : "none",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                };
                                if (isTextCol) {
                                  return (
                                    <ObsCell
                                      key={`${bi}-${ci}`}
                                      value={String(v)}
                                      baseSx={cellSx}
                                    />
                                  );
                                }
                                return (
                                  <Box
                                    component="td"
                                    key={`${bi}-${ci}`}
                                    sx={cellSx}
                                  >
                                    {String(v)}
                                  </Box>
                                );
                              });
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

function RegularLabel({ row }: { row: TableRow }) {
  const base: Record<string, any> = {
    ml: "8px",
    flex: 1,
    display: "flex",
    alignItems: "center",
    pl: "8px",
    pr: "24px",
    overflow: "hidden",
    fontSize: 14,
    fontFamily: FONT,
    boxSizing: "border-box",
  };

  let sx: Record<string, any>;

  switch (row.kind) {
    case "service":
      sx = {
        ...base,
        bgcolor: C.white,
        color: C.cellText,
        borderLeft: bdr,
        borderBottom: bdr,
      };
      break;
    case "separator":
      sx = {
        ml: "8px",
        flex: 1,
        bgcolor: C.separatorBg,
        borderLeft: bdr,
        borderBottom: bdr,
      };
      return <Box sx={sx} />;
    case "category-total": {
      const last = (row as any).isLast;
      sx = {
        ...base,
        bgcolor: C.white,
        color: C.cellText,
        borderLeft: bdr,
        borderBottom: last ? "none" : bdr,
        borderRadius: last ? "0 0 0 8px" : undefined,
      };
      break;
    }
    case "section-total":
      sx = {
        ...base,
        bgcolor: C.categoryBg,
        color: C.cellText,
        fontWeight: 700,
        borderTop: bdr,
        borderLeft: bdr,
        borderBottom: bdr,
        borderRadius: "8px 0 0 8px",
      };
      break;
    default:
      sx = base;
  }

  return (
    <Box sx={sx}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: "inherit",
          color: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: "24px",
          fontFamily: FONT,
        }}
      >
        {(row as any).label}
      </Typography>
    </Box>
  );
}

function RegularDataCell({
  row,
  colIndex,
  value,
  cellW,
}: {
  row: TableRow;
  colIndex: number;
  value: string | number;
  cellW?: number;
}) {
  const w = cellW ?? COL_WIDTHS[colIndex] ?? SUB_W;
  const isLast = colIndex === 3;
  const base: Record<string, any> = {
    width: w,
    minWidth: w,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: "10px",
    pl: "4px",
    fontSize: 13,
    fontFamily: FONT,
    overflow: "hidden",
    boxSizing: "border-box",
  };

  let sx: Record<string, any>;

  switch (row.kind) {
    case "service":
      sx = {
        ...base,
        bgcolor: C.white,
        color: C.cellText,
        borderBottom: bdr,
        borderRight: !isLast ? bdr : "none",
      };
      break;
    case "category-total": {
      const lastRow = (row as any).isLast;
      sx = {
        ...base,
        bgcolor: C.white,
        color: C.cellText,
        borderBottom: lastRow ? "none" : bdr,
        borderRight: !isLast ? bdr : "none",
      };
      break;
    }
    case "section-total":
      sx = {
        ...base,
        bgcolor: C.categoryBg,
        color: C.cellText,
        fontWeight: 700,
        borderTop: bdr,
        borderBottom: bdr,
        borderRight: !isLast ? bdr : "none",
      };
      break;
    default:
      sx = { ...base, bgcolor: C.white, color: C.cellText };
  }

  return (
    <Box sx={sx}>
      <Typography
        sx={{
          fontSize: 13,
          color: "inherit",
          fontWeight: "inherit",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "normal",
          fontFamily: FONT,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default BudgetComparisonModal;
